<template>
  <div class="table-stat-card" :class="cardClasses" @click="handleClick">
    <!-- Header -->
    <div class="card-header">
      <h3 class="card-title">{{ config.title }}</h3>
      <span v-if="config.badge" :class="badgeClasses">{{ config.badge }}</span>
    </div>

    <!-- Table Content -->
    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th
              v-for="column in config.columns"
              :key="column.key"
              :class="getHeaderClasses(column)"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in tableData"
            :key="`row-${index}`"
            class="table-row"
            @click.stop="handleRowClick(row, index)"
          >
            <td
              v-for="column in config.columns"
              :key="`${index}-${column.key}`"
              :class="getCellClasses(column)"
            >
              <!-- Category column with icon support -->
              <span
                v-if="column.key === 'category'"
                class="category-cell flex items-center"
              >
                <i
                  v-if="config.showTableIcon && row.icon"
                  class="material-icons text-gray-500 mr-3 text-lg"
                >
                  {{ row.icon }}
                </i>
                {{ row[column.key] }}
              </span>
              <!-- Count column with data mapping -->
              <span
                v-else-if="column.key === 'count'"
                class="count-cell font-semibold text-gray-900"
              >
                {{ formatValue(getMappedValue(row.count)) }}
              </span>
              <!-- Type column -->
              <span v-else-if="column.key === 'type'" class="type-cell">
                {{ row[column.key] }}
              </span>
              <!-- Default number cell -->
              <span v-else class="number-cell">
                {{ formatValue(row[column.key]) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "TableStatCard",
  props: {
    config: {
      type: Object,
      required: true
    },
    data: {
      type: [Object, Array],
      default: () => ({})
    }
  },
  computed: {
    cardClasses() {
      return [
        "bg-white",
        "rounded-xl",
        "shadow-sm",
        "border",
        "border-gray-100",
        "p-6",
        "hover:shadow-md",
        "transition-all",
        "duration-300",
        "cursor-pointer",
        "hover:border-gray-200"
      ];
    },

    badgeClasses() {
      const colorMap = {
        green: "bg-emerald-100 text-emerald-700",
        blue: "bg-blue-100 text-blue-700",
        red: "bg-red-100 text-red-700",
        yellow: "bg-yellow-100 text-yellow-700",
        purple: "bg-purple-100 text-purple-700"
      };

      return [
        "inline-flex",
        "items-center",
        "px-2.5",
        "py-0.5",
        "rounded-full",
        "text-xs",
        "font-medium",
        colorMap[this.config.badgeColor] || colorMap.blue
      ];
    },

    tableData() {
      // If config has tableData (new format), use it
      if (this.config.tableData) {
        return this.config.tableData;
      }

      // Legacy support for array data
      if (Array.isArray(this.data)) {
        return this.data;
      }

      // Convert object data to array format
      if (this.config.dataKey && this.data[this.config.dataKey]) {
        return this.data[this.config.dataKey];
      }

      // Default data structure for reports/imports
      return this.data.items || [];
    }
  },
  methods: {
    getHeaderClasses(column) {
      const baseClasses = [
        "px-3",
        "py-3",
        "text-xs",
        "font-semibold",
        "text-gray-600",
        "uppercase",
        "tracking-wider",
        "border-b",
        "border-gray-200"
      ];

      if (column.align) {
        baseClasses.push(`text-${column.align}`);
      } else {
        baseClasses.push("text-left");
      }

      return baseClasses;
    },

    getCellClasses(column) {
      const baseClasses = [
        "px-3",
        "py-4",
        "whitespace-nowrap",
        "text-sm",
        "border-b",
        "border-gray-100"
      ];

      if (column.align) {
        baseClasses.push(`text-${column.align}`);
      } else {
        baseClasses.push("text-left");
      }

      return baseClasses;
    },

    formatValue(value) {
      if (typeof value === "number") {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(1) + "M";
        }
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + "K";
        }
        return value.toLocaleString();
      }
      return value || "0";
    },

    getMappedValue(countKey) {
      // If it's a direct value, return it
      if (typeof countKey === "number") {
        return countKey;
      }

      // Map the count key to actual data value
      return this.data[countKey] || 0;
    },

    handleClick() {
      this.$emit("card-click", {
        type: "table",
        config: this.config,
        data: this.data
      });
    },

    handleRowClick(row, index) {
      this.$emit("row-click", {
        row,
        index,
        config: this.config
      });
    }
  }
};
</script>

<style scoped>
.table-stat-card {
  position: relative;
  overflow: hidden;
}

.table-stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.table-stat-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
}

.table-row {
  transition: all 0.2s ease;
  cursor: pointer;
  border-left: 3px solid transparent;
}

.table-row:last-child td {
  border-bottom: none;
}

.category-cell,
.type-cell {
  font-weight: 500;
  color: #374151;
}

.count-cell {
  font-weight: 600;
  color: #1f2937;
  font-variant-numeric: tabular-nums;
}

.number-cell {
  font-weight: 600;
  color: #1f2937;
  font-variant-numeric: tabular-nums;
}

.material-icons {
  transition: color 0.2s ease;
}

.table-row:hover .material-icons {
  color: #3b82f6 !important;
}

.table-row:hover {
  background-color: #f8fafc;
  border-left: 3px solid #3b82f6;
  transform: translateX(2px);
}

/* Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-stat-card {
  animation: slideInUp 0.4s ease-out;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .table-container {
    margin: -0.5rem;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
}
</style>
